import { useAsyncPreviewOrderPrice, usePreviewOrderPrice } from '@/hooksApi/useOrder';
import { largestSize } from '@/components/OrderForm/MultiBoxSelector';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import {
  BasicProduct,
  Order,
  OrderOptionEnum,
  PaymentType,
  Preview,
  ProductDetails,
  ShipmentFeeType,
} from '@/types';
import {
  adjustProductListByOrderType,
  attachCountryCodeToPhone,
  checkPhoneNumberValidation,
  getWarehouseAddressType,
} from '@/utils';
import { FormInstance, message } from 'antd';
import { Store } from 'antd/es/form/interface';
import debounce from 'lodash.debounce';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Long enough to swallow a burst of typing, short enough that the fee still
// feels live once the merchant stops.
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * Refusals that are an ANSWER, not "the form is not finished yet".
 *
 * `useAsyncPreviewOrderPrice` is deliberately silent (`defaultErrorMessage: ''`)
 * because preview fires on every keystroke, and toasting each half-typed form
 * would be unusable. That is right for validation noise and wrong for these:
 * the backend has fully understood the order and is telling the merchant it
 * cannot be priced.
 *
 * Reported live 2026-08-26: a province rule sending Basra to Boxy Save, with
 * temperature control on. The backend answered with the exact sentence the
 * merchant needed — Boxy Save does not carry cold — and the form swallowed it,
 * zeroed the summary, and displayed "Customer Will Pay 0 IQD". A price of zero
 * is not a refusal; it reads as a free shipment.
 */
/**
 * ⭐ TAKEN FROM THE BACKEND'S `ERROR_CODE` CONSTANTS, NOT FROM MEMORY. An
 * earlier version of this list carried `SERVICE_UNAVAILABLE_FOR_PROVINCE`,
 * which does not exist anywhere in core-service, and omitted
 * `TEMP_CONTROL_NO_ELIGIBLE_CARRIER`, which does. Both mistakes fail the same
 * silent way: the matcher never fires and the merchant is back to a bare
 * 0 IQD — code that looks implemented, reviews clean, and does nothing.
 *
 * The live set is every ERROR_CODE on an order-refusal exception:
 *   UnknownServiceCodeException      SERVICE_CODE_UNKNOWN
 *                                    SERVICE_CODE_NOT_ENTITLED
 *                                    SERVICE_NO_TEMPERATURE_CONTROL
 *   ServiceLaneIneligibleException   SERVICE_NO_ELIGIBLE_CARRIER
 *   TemperatureControlIneligible…    TEMP_CONTROL_NO_ELIGIBLE_CARRIER
 *
 * An allow-list rather than "is the code a string": the ordinary
 * form-not-finished 422 answers `code: 422` (an int) but other string codes
 * exist that are NOT order refusals (SLOT_*), so type is not the question —
 * membership is.
 */
export const BLOCKING_PREVIEW_CODES = [
  'SERVICE_NO_TEMPERATURE_CONTROL',
  'SERVICE_NO_ELIGIBLE_CARRIER',
  'TEMP_CONTROL_NO_ELIGIBLE_CARRIER',
  'SERVICE_CODE_UNKNOWN',
  'SERVICE_CODE_NOT_ENTITLED',
];

const orderPriceDefaultValue = {
  orders_value: 0,
  orders_value_with_shipment_fee: 0,
  orders_shipment_fee: 0,
  orders_value_with_platform_shipment_fee: 0,
  orders_custom_shipment_fee: 0,
};

enum FormSectionsEnum {
  CONTACT,
  PRODUCT,
  PICKUP_ADDRESS,
  PACKAGE_SIZE,
  PAYMENT,
  PHONE_NUMBER, // not a section
}

interface SectionFields {
  sectionName: FormSectionsEnum;
  fieldNames: string[];
  isRequired?: boolean;
  validationMessage?: string;
}

const sectionFields: SectionFields[] = [
  {
    sectionName: FormSectionsEnum.CONTACT,
    fieldNames: [
      // 'name',
      // 'address',
      'province',
      'region_uid',
      'phoneNumberPrimary',
    ],
    isRequired: true,
    validationMessage: 'please_fill_customer_details',
  },
  {
    sectionName: FormSectionsEnum.PRODUCT,
    fieldNames: ['products'],
    isRequired: true,
    validationMessage: 'please_add_at_least_one_product',
  },
  {
    sectionName: FormSectionsEnum.PICKUP_ADDRESS,
    fieldNames: ['saved_pick_up_address_uid'],
    isRequired: true,
    validationMessage: 'please_select_pickup_address',
  },
  {
    sectionName: FormSectionsEnum.PACKAGE_SIZE,
    fieldNames: ['isFragile', 'temperatureControl', 'packageSize'],
  },
  {
    sectionName: FormSectionsEnum.PAYMENT,
    fieldNames: ['customShipmentFee', 'CustomerOrMerchant'],
  },
  {
    sectionName: FormSectionsEnum.PHONE_NUMBER,
    fieldNames: [],
    validationMessage: 'please_enter_valid_phone_number',
  },
];

const CREATE_PICKUP_ADDRESS_MESSAGE = 'please_create_pickup_address';

const useUpdateOrderPricePreview = (
  productsStore: BasicProduct[],
  form: FormInstance<any>,
  ignorePickupAddressValidation?: boolean,
  onCarrierPreviewTrigger?: (orderData: Order) => void,
) => {
  /*
   ? IMPROVEMENTS:
      debouncing onChange function of phoneNumber and name inputs 
  */
  const [isPreviewCalled, setIsPreviewCalled] = useState<boolean>(false);
  // The sentence the backend gave us, when it gave us one worth showing.
  const [previewBlockedReason, setPreviewBlockedReason] = useState<string | null>(null);
  // BOXY-597 — and its code, so a page can tell "pick another service" apart.
  const [previewBlockedCode, setPreviewBlockedCode] = useState<string | null>(null);

  const [pendingNewProduct, setPendingNewProduct] = useState<BasicProduct | null>(null);

  const [customShipmentFee, setCustomShipmentFee] = useState<number | undefined>(undefined);

  const [orderPrice, setOrderPrice] = useState<Preview>(orderPriceDefaultValue);
  const [invalidFields, setInvalidFields] = useState<Set<FormSectionsEnum>>(new Set());

  const { mutate: previewOrderPrice, isLoading: orderPriceLoading } = useAsyncPreviewOrderPrice();
  const { data: pickupAddressesData } = useFetchPickupAddresses();

  const { t } = useTranslation();

  const getInvalidMessage = (message?: string) => {
    const pickupAddressErrorMessage =
      sectionFields[FormSectionsEnum.PICKUP_ADDRESS].validationMessage;
    if (message !== pickupAddressErrorMessage) return message;
    const pickupWarehouseList = getWarehouseAddressType(pickupAddressesData?.object.items);
    return pickupWarehouseList.length === 0
      ? CREATE_PICKUP_ADDRESS_MESSAGE
      : pickupAddressErrorMessage;
  };

  const transformValuesToOrder = (
    values: Store,
    ready_to_pick_up?: boolean,
    hasVerifyOptions?: boolean,
  ) => {
    const primaryPhoneNumber = attachCountryCodeToPhone(
      values?.phoneNumberPrimary,
      values?.countryCodePrimary,
    );

    const secondaryPhoneNumber = attachCountryCodeToPhone(
      values?.secondaryPhoneNumber,
      values?.countryCodeSecondary,
    );

    const productList: ProductDetails[] = values.products ?? productsStore ?? [];

    const orderData: Order = {
      type: values?.orderType,
      fee_customer_payable:
        values?.CustomerOrMerchant === ShipmentFeeType.BY_CUSTOMER
          ? (values.customShipmentFee ?? customShipmentFee)
          : null,
      is_fragile: values?.isFragile || false,
      temperature_control: values?.temperatureControl || false,
      // Omitted, NOT null, when the merchant hasn't picked one: the backend
      // then resolves the service from their standing rules (which include
      // per-province overrides an explicit code would silently defeat). It
      // also lands in the preview payload, so the quoted fee is the fee for
      // the service actually chosen.
      service_code: values?.serviceCode || undefined,
      id_check: values?.isIdChecked || false,
      clue_verification: values?.isClueVerification || false,
      ready_to_pick_up: !!ready_to_pick_up,
      shipment_fee_type: values?.CustomerOrMerchant || ShipmentFeeType.BY_MERCHANT,
      payment_type: values?.CodOrPrepaid || PaymentType.COLLECT_ON_DELIVERY,
      pick_up_type: values?.pickupOrDropoff || 'PICK_UP',
      description: values?.deliveryInstruction || '',
      custom_id: values?.orderId || null,
      products: productList.map((product) => ({
        title: product?.title || '',
        price: product?.price,
        quantity: Number(product?.quantity) || 1,
        uid: product?.uid || undefined,
        product_catalog_uid: product?.product_catalog_uid || undefined,
        is_fragile: product?.is_fragile ?? false,
      })),
      saved_pick_up_address_uid: values?.saved_pick_up_address_uid || '',
      // A multi-box order is the size of its largest box (the backend stores
      // it so). In the per-box list the size tiles are unmounted, so
      // `packageSize` was absent and every mixed order went out as 'S'.
      size:
        Array.isArray(values?.boxes) && values.boxes.length >= 2
          ? largestSize(values.boxes)
          : values?.packageSize || 'S',
      /*
       * BOXY-301 §5.1 — the box list, and it goes HERE rather than in the
       * submit handler on purpose: this transformer feeds BOTH the price
       * preview and the create request, so quoting and charging are the same
       * payload by construction. Putting it only on create is how a merchant
       * is shown one number and billed another.
       *
       * Spread-or-nothing: a single-box order sends no `boxes` key at all, so
       * its request is byte-identical to today's and the backend's
       * `prohibited`/`min:2` rules never see it.
       */
      ...(Array.isArray(values?.boxes) && values.boxes.length >= 2
        ? { boxes: values.boxes.map((box: { size: string }) => ({ size: box.size })) }
        : {}),
      contact: {
        full_name: values?.name || '',
        address_text: values?.address || '',
        phone: primaryPhoneNumber ?? '',
        additional_phones: secondaryPhoneNumber ? [secondaryPhoneNumber] : [],
        region_uid: values?.region_uid || '',
      },
    };
    if (values.freezed_pick_up_address_title) {
      // if freezed_pick_up_address_title has value =SO=> the order status is not new or undefined

      //setting pick_up_address_uid
      orderData.pick_up_address_uid = values?.saved_pick_up_address_uid || '';
    } else {
      //setting saved_pick_up_address_uid
      orderData.saved_pick_up_address_uid = values?.saved_pick_up_address_uid || '';
    }

    if (hasVerifyOptions) {
      orderData.options = [
        ...(values?.isIdChecked ? [{ type: OrderOptionEnum.ID_CHECK }] : []),
        ...(values?.isClueVerification
          ? [{ type: OrderOptionEnum.CLUE_VERIFICATION, value: values.clueLink }]
          : []),
      ];
    }
    return orderData;
  };

  const getInvalidSections = (orderPayload: Store, checkProduct?: boolean) => {
    const invalidSections: FormSectionsEnum[] = [];

    const primaryPhoneNumber = attachCountryCodeToPhone(
      orderPayload?.phoneNumberPrimary,
      orderPayload?.countryCodePrimary,
    );

    const isPhoneValid = checkPhoneNumberValidation(
      orderPayload?.phoneNumberPrimary,
      orderPayload?.countryCodePrimary,
    );

    const isContactValid = !!(
      // orderPayload?.name &&
      (
        primaryPhoneNumber &&
        // orderPayload?.address &&
        orderPayload?.region_uid
      )
    );
    const isPickupAddressValid =
      !!orderPayload.saved_pick_up_address_uid || !!orderPayload.freezed_pick_up_address_title;
    const isProductValid = (orderPayload.products ?? productsStore ?? []).length > 0;

    if (!isContactValid) invalidSections.push(FormSectionsEnum.CONTACT);
    if (!ignorePickupAddressValidation && !isPickupAddressValid)
      invalidSections.push(FormSectionsEnum.PICKUP_ADDRESS);
    if (!isProductValid && checkProduct) invalidSections.push(FormSectionsEnum.PRODUCT);
    // if (!isPhoneValid) invalidSections.push(RequiredFieldsEnum.PHONE_NUMBER);

    return invalidSections;
  };

  const getPreviousInvalidSections = (changedSectionIndex: number, _formValues: Store) => {
    const invalidSectionsMessage: FormSectionsEnum[] = [];

    const formValues: Store = {
      ..._formValues,
      products: _formValues.products?.length > 0 ? _formValues.products : undefined,
    };

    //if is required section => check
    for (let index = 0; index <= changedSectionIndex; index++) {
      //
      const section = sectionFields[index];
      if (section.validationMessage) {
        // if !!validationMessage => is required
        const isValid = section.fieldNames.every((field) => {
          return !!formValues[field];
        });
        if (!isValid) invalidSectionsMessage.push(section.sectionName);
      }
    }
    if (changedSectionIndex) {
      const isPrimaryPhoneValid = checkPhoneNumberValidation(
        formValues?.phoneNumberPrimary,
        formValues?.countryCodePrimary,
      );

      const isSecondaryPhoneValid = formValues?.secondaryPhoneNumber
        ? checkPhoneNumberValidation(
            formValues?.secondaryPhoneNumber,
            formValues?.countryCodeSecondary,
          )
        : true;

      if (!isPrimaryPhoneValid || !isSecondaryPhoneValid) {
        invalidSectionsMessage.push(FormSectionsEnum.PHONE_NUMBER);
      }
    }
    return invalidSectionsMessage;
  };

  const handleValidationError = (newInvalidSection: FormSectionsEnum[]) => {
    newInvalidSection.forEach((section) => {
      const validationMessage = getInvalidMessage(sectionFields[section].validationMessage);

      if (validationMessage) message.error(t(validationMessage));
    });
  };

  const checkOrderFormValidation = (orderPayload: Store, checkingForProduct?: boolean) => {
    let invalidFieldList = getPreviousInvalidSections(checkingForProduct ? 1 : 3, orderPayload);
    if (ignorePickupAddressValidation) {
      invalidFieldList = invalidFieldList.filter((s) => s !== FormSectionsEnum.PICKUP_ADDRESS);
    }
    handleValidationError(invalidFieldList);

    return invalidFieldList.length === 0;
  };

  // R54 (CA.1) — one quote per pause in typing, not one per keystroke.
  //
  // `shouldUpdatePrice` lists `products` and `customShipmentFee`, and antd fires
  // onValuesChange per character, so typing a four-digit price used to send four
  // `/orders/bulk-preview` calls. Round 54's browser run counted seven 422s
  // filling one form: every intermediate keystroke is a half-typed order, and
  // the last one is the only answer anyone reads.
  //
  // Every caller awaiting a superseded keystroke is resolved with the SAME
  // final price, so no caller hangs and no earlier quote can land after a later
  // one and write a stale fee into the card.
  const pendingResolvers = useRef<((price: Preview) => void)[]>([]);

  const settleAll = (price: Preview) => {
    const waiting = pendingResolvers.current;
    pendingResolvers.current = [];
    waiting.forEach((resolve) => resolve(price));
  };

  // Held in a ref and re-assigned every render: the debounced function itself is
  // created once, so without this it would keep the closure from the FIRST
  // render and read a stale `isPreviewCalled` forever.
  const requestPreview = useRef<(order: ReturnType<typeof transformValuesToOrder>) => void>();

  requestPreview.current = (orderPayload) => {
    previewOrderPrice(
      { orders: [orderPayload], ready_to_pick_up: false },
      {
        onSuccess: (response) => {
          setPreviewBlockedReason(null);
          setPreviewBlockedCode(null);
          settleAll({ ...response.object.total });
          invalidFields.clear();
          setInvalidFields(invalidFields);

          if (!isPreviewCalled) {
            setTimeout(() => {
              setIsPreviewCalled(true);
            }, 500);
          }
        },
        onError: (error) => {
          invalidFields.add(0);
          setInvalidFields(invalidFields);

          // Only a DEFINITIVE refusal is shown. An incomplete form 422s too,
          // and surfacing that would put an error under the price on every
          // half-filled order — which is why this hook is silent by default.
          const data = error?.response?.data;
          const isBlocking = !!data?.code && BLOCKING_PREVIEW_CODES.includes(String(data.code));

          setPreviewBlockedReason(isBlocking && data?.message ? String(data.message) : null);
          setPreviewBlockedCode(isBlocking && data?.message ? String(data.code) : null);

          settleAll(orderPriceDefaultValue);
        },
      },
    );
  };

  const debouncedPreview = useRef(
    debounce(
      (orderPayload: ReturnType<typeof transformValuesToOrder>) =>
        requestPreview.current?.(orderPayload),
      PREVIEW_DEBOUNCE_MS,
    ),
  ).current;

  useEffect(() => () => debouncedPreview.cancel(), [debouncedPreview]);

  const updateOrderPrice = (formData: Store): Promise<Preview> => {
    const orderPayload = transformValuesToOrder(formData);

    return new Promise((resolve) => {
      pendingResolvers.current.push(resolve);
      debouncedPreview(orderPayload);
    });
  };

  const handleChangeCustomShipmentFee = (
    value?: number,
    skipFormUpdate?: boolean,
    callFormUpdate: boolean = true,
  ) => {
    setCustomShipmentFee(value);

    if (!skipFormUpdate) {
      const formValues = form.getFieldsValue();
      if (callFormUpdate)
        handleFormUpdated(
          { customShipmentFee: value },
          { ...formValues, customShipmentFee: value },
        );
    }
  };

  const handleProductChanged = (
    products: ProductDetails[],
    callFormUpdate: boolean = true,
    shouldAddPendingNewProduct: boolean = true,
  ) => {
    const formValues = form.getFieldsValue();
    if (callFormUpdate)
      handleFormUpdated({ products }, { ...formValues, products }, shouldAddPendingNewProduct);
  };

  const setPackageSize = (size: string, callFormUpdate: boolean = true) => {
    const formValues = form.getFieldsValue();

    if (callFormUpdate) handleFormUpdated({ packageSize: size }, formValues);
  };

  const shouldUpdatePrice = (changedItem: string): boolean => {
    const keysToCheck = [
      'products',
      'customShipmentFee',
      // 'province',
      'region_uid',
      'isFragile',
      'temperatureControl',
      'orderType',
      'packageSize',
      // Box count and per-box sizes decide the fee and the lane checks too.
      'boxes',
      'saved_pick_up_address_uid',
      // The chosen service decides the price, so a change must re-quote.
      // Omitting it meant the merchant saw the previous service's fee and was
      // charged the new one — and only on a VALID form, because an invalid
      // form re-quotes anyway via the invalidFields recovery branch below.
      'serviceCode',
    ];
    return (
      keysToCheck.includes(changedItem) ||
      (!!customShipmentFee && changedItem === 'CustomerOrMerchant')
    );
  };

  const handleFormUpdated = async (
    changedValues: any,
    allValues: Store,
    shouldAddPendingNewProduct: boolean = true,
  ) => {
    if (Object.keys(allValues).length === 0) return;
    const changedItem = Object.keys(changedValues)[0];

    let productList = allValues.products ?? productsStore;

    productList = adjustProductListByOrderType(
      pendingNewProduct && shouldAddPendingNewProduct
        ? [...productList, pendingNewProduct]
        : productList,
      allValues.orderType,
    );

    const formValues = { ...allValues, products: productList };
    let newOrderPrice = orderPriceDefaultValue;

    const currentInvalidFields = getInvalidSections(formValues, true);
    const shouldBeCalled = shouldUpdatePrice(changedItem);

    //2nd scenario =>  if we should update but also we had invalid fields => if user changed the invalid field=> update
    if (shouldBeCalled) {
      if (currentInvalidFields.length === 0) {
        // formData is valid
        newOrderPrice = await updateOrderPrice(formValues);
      } else {
        // if we should update but formData is invalid field => if user changed the invalid field=> update
        setInvalidFields(new Set(currentInvalidFields));

        // if (onCarrierPreviewTrigger) {
        //   // Transform formValues to Order format with proper contact nesting
        //   const orderPayload = transformValuesToOrder(formValues);
        //   onCarrierPreviewTrigger(orderPayload);
        // }
        /*
        ! this lines have been commented to prevent showing error alerts
          const changedSectionIndex = sectionFields.findIndex((item) =>
            item.fieldNames.includes(changedItem),
          );
          const previousInvalidSectionsMessage = getPreviousInvalidSections(
            changedSectionIndex,
            formValues,
          );
 
          if (changedSectionIndex) handleValidationError(previousInvalidSectionsMessage);
        */
      }
      setOrderPrice(newOrderPrice);
    } else if (currentInvalidFields.length === 0 && invalidFields.size > 0) {
      newOrderPrice = await updateOrderPrice(formValues);
      setOrderPrice(newOrderPrice);
    }
  };

  return {
    orderPrice,
    setOrderPrice,
    orderPriceLoading,
    handleFormUpdated,
    customShipmentFee,
    setCustomShipmentFee: handleChangeCustomShipmentFee,
    setPackageSize,
    transformValuesToOrder,
    checkOrderFormValidation,
    handleProductChanged,
    pendingNewProduct,
    setPendingNewProduct,
    isPreviewCalled,
    previewBlockedReason,
    previewBlockedCode,
  };
};

export default useUpdateOrderPricePreview;
